if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dotsLink: this.querySelectorAll('[js-media-gallery="link"]'),
          playButton: this.querySelector('[js-thumbnail="play"]'),
          pauseButton: this.querySelector('[js-thumbnail="pause"]')
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initSlideObserver();

        if (this.elements.dotsLink.length > 1) {
          this.attachCustomButtonListeners();
          this.playActiveMedia();
          this.initSlideObserver();
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initSlideObserver() {
        const observer = new IntersectionObserver(this.handleIntersection.bind(this), {
          root: this.elements.viewer,
          threshold: 1
        });

        this.elements.viewer.querySelectorAll('.product__media-item').forEach(observer.observe.bind(observer));
      }

      handleIntersection(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setActiveSlide(entry.target);
          }
        });
      }

      setActiveSlide(activeSlide) {
        this.elements.viewer
          .querySelectorAll('.product__media-item')
          .forEach((slide) => slide.classList.remove('is-active'));
        activeSlide.classList.add('is-active');

        const activeButtonIndex = Array.from(this.elements.dotsLink).findIndex(
          (button, index) => this.getMediaIdForButton(index) === activeSlide.dataset.mediaId
        );

        this.elements.dotsLink.forEach((button, index) => {
          button.classList.remove('is-active');
          if (index === activeButtonIndex) {
            button.classList.add('is-active');
          }
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia?.classList?.remove('hidden');
          const parent = activeMedia.parentElement;
          const firstElement = parent.firstElementChild;

          if (firstElement !== activeMedia) {
            const nextSibling = activeMedia.nextSibling;
            parent.prepend(activeMedia);

            nextSibling ? parent.insertBefore(firstElement, nextSibling) : parent.appendChild(firstElement);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia() {
        if (!this.elements.playButton || !this.elements.pauseButton) return;

        const toggleVideo = () => {
          const video = this.querySelector('video');
          if (!video) return;

          if (video.paused) {
            video.play();
            this.elements.playButton.classList.add('hidden');
            this.elements.pauseButton.classList.remove('hidden');
            return;
          }

          video.pause();
          this.elements.pauseButton.classList.add('hidden');
          this.elements.playButton.classList.remove('hidden');
        };

        this.elements.playButton.addEventListener('click', toggleVideo);
        this.elements.pauseButton.addEventListener('click', toggleVideo);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      attachCustomButtonListeners() {
        this.elements.dotsLink.forEach((button, index) => {
          button.addEventListener('click', () => {
            this.setActiveMedia(this.getMediaIdForButton(index), false);
            this.elements.dotsLink.forEach((btn) => btn.classList.remove('is-active'));
            button.classList.add('is-active');
          });
        });
      }

      getMediaIdForButton(index) {
        const visibleSlides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]')).filter(
          (slide) => !slide.classList.contains('hidden')
        );
        return visibleSlides[index]?.dataset.mediaId || null;
      }
    }
  );
}
document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('.product__slider-buttons .slideshow__control-wrapper .slider-counter__link')
    .forEach(
      (dotButtons, index) =>
        index >= document.querySelectorAll('.product__media-list li:not(.hidden)').length && dotButtons.remove()
    );
});
